/**
 * Contexto de Navegação de Abas - CIPA Fácil
 *
 * Permite que componentes filhos naveguem entre abas sem precisar
 * de prop drilling. Usado principalmente pelo GateGuard para
 * redirecionar usuários para a aba correta.
 *
 * @author CIPA Fácil Team
 * @date 2026-02-02
 */

import React, {
  createContext,
  useContext,
  ReactNode,
  useCallback,
} from "react";

/**
 * Abas disponíveis no sistema
 */
export type TabId =
  | "dashboard"
  | "cronograma"
  | "votacao"
  | "relatorios"
  | "plano-trabalho"
  | "ata-digital"
  | "gestao-mandatos"
  | "gestao-membros"
  | "cronograma-reunioes"
  | "empresas"
  | "documentos"
  | "ajuda"
  | "admin";

/**
 * Mapeamento de FlowStep para TabId
 */
const FLOW_STEP_TO_TAB: Record<string, TabId> = {
  empresa: "empresas",
  mandato: "gestao-mandatos",
  membros: "gestao-membros",
  cronograma: "cronograma-reunioes",
  reunioes: "cronograma-reunioes",
  ata: "ata-digital",
  relatorios: "relatorios",
  "plano-trabalho": "plano-trabalho",
  documentos: "documentos",
};

/**
 * Interface do contexto de navegação
 */
interface TabNavigationContextType {
  /** Aba atualmente ativa */
  activeTab: TabId;

  /** Função para mudar de aba */
  setActiveTab: (tab: TabId) => void;

  /** Navega para uma aba específica */
  navigateTo: (tab: TabId) => void;

  /** Navega para a aba correspondente a um FlowStep */
  navigateToFlowStep: (flowStep: string) => void;
}

const TabNavigationContext = createContext<
  TabNavigationContextType | undefined
>(undefined);

/**
 * Hook para acessar a navegação de abas
 */
export function useTabNavigation() {
  const context = useContext(TabNavigationContext);
  if (!context) {
    throw new Error(
      "useTabNavigation deve ser usado dentro de um TabNavigationProvider",
    );
  }
  return context;
}

/**
 * Hook opcional - retorna null se não estiver dentro do provider
 * Útil para componentes que podem ou não estar dentro do contexto
 */
export function useTabNavigationOptional() {
  return useContext(TabNavigationContext);
}

interface TabNavigationProviderProps {
  children: ReactNode;
  activeTab: TabId;
  setActiveTab: (tab: TabId) => void;
}

/**
 * Provider de navegação de abas
 *
 * @example
 * ```tsx
 * // No App.tsx
 * <TabNavigationProvider activeTab={activeTab} setActiveTab={setActiveTab}>
 *   {renderTabContent()}
 * </TabNavigationProvider>
 * ```
 */
export function TabNavigationProvider({
  children,
  activeTab,
  setActiveTab,
}: TabNavigationProviderProps) {
  const navigateTo = useCallback(
    (tab: TabId) => {
      console.log(`[TabNavigation] Navegando para: ${tab}`);
      setActiveTab(tab);
    },
    [setActiveTab],
  );

  const navigateToFlowStep = useCallback(
    (flowStep: string) => {
      const tab = FLOW_STEP_TO_TAB[flowStep];
      if (tab) {
        console.log(`[TabNavigation] FlowStep "${flowStep}" -> Tab "${tab}"`);
        setActiveTab(tab);
      } else {
        console.warn(`[TabNavigation] FlowStep desconhecido: ${flowStep}`);
      }
    },
    [setActiveTab],
  );

  return (
    <TabNavigationContext.Provider
      value={{
        activeTab,
        setActiveTab,
        navigateTo,
        navigateToFlowStep,
      }}
    >
      {children}
    </TabNavigationContext.Provider>
  );
}

/**
 * Converte uma ctaRoute do GateGuard para TabId
 * @param ctaRoute - Rota no formato antigo (ex: "/dashboard?tab=empresas")
 * @returns TabId correspondente ou null
 */
export function parseCtaRouteToTab(ctaRoute: string): TabId | null {
  // Formato: "/dashboard?tab=gestao-mandatos"
  const match = ctaRoute.match(/[?&]tab=([^&]+)/);
  if (match) {
    return match[1] as TabId;
  }

  // Formato: "/planos" - rota externa, não é uma tab
  if (ctaRoute === "/planos") {
    return null;
  }

  return null;
}

export default TabNavigationContext;
